import { helpFor } from "./metric-help.js";
import { toggleExplanations, useExplanationsVisible } from "./explanations.js";
import { signalBasisFor, signalToneFor } from "./metric-signals.js";
import "./drawer.css";
import { useState, useEffect, useCallback, type ReactNode } from "react";
import { DEFAULT_THEME, type ThemeName } from "@senesight/tokens";

/**
 * E8.1 signature components — they render the LOCKED design system's exact classes (from
 * @senesight/tokens/theme.css, ported byte-identical from the delivery-intelligence demo), so they
 * look identical to the demo. Trust semantics are first-class and accessible: RAG is dual-encoded
 * (dot + icon + label, never colour-alone), confidence shows an honest N/A, and provenance drills to
 * evidence. The app defaults to the demo's dark theme; the .tgl toggle switches to light.
 */

// --- theme (dark-default; .tgl toggle) -------------------------------------
const THEME_STORAGE_KEY = "senesight.theme";

function validTheme(value: string | null | undefined): ThemeName | undefined {
  return value === "dark" || value === "light" ? value : undefined;
}

function storedTheme(): ThemeName | undefined {
  try {
    return validTheme(window.localStorage.getItem(THEME_STORAGE_KEY));
  } catch {
    return undefined;
  }
}

export function applyTheme(theme: ThemeName): void {
  document.documentElement.dataset.theme = theme;
}
/** A valid URL override wins for this load; only the toggle changes the saved preference. */
export function initTheme(): ThemeName {
  const current =
    validTheme(new URLSearchParams(window.location.search).get("theme")) ??
    storedTheme() ??
    DEFAULT_THEME;
  applyTheme(current);
  return current;
}
export function ThemeToggle(): ReactNode {
  const [theme, setTheme] = useState<ThemeName>(
    () =>
      (typeof document !== "undefined" && validTheme(document.documentElement.dataset.theme)) ||
      DEFAULT_THEME,
  );
  useEffect(() => applyTheme(theme), [theme]);
  const toggle = useCallback(() => {
    const next = theme === "dark" ? "light" : "dark";
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
      // Keep the current session usable even when storage is blocked.
    }
    setTheme(next);
  }, [theme]);
  return (
    <button
      type="button"
      className="tgl"
      role="switch"
      aria-checked={theme === "light"}
      aria-label="Toggle colour theme"
      onClick={toggle}
    >
      <span className="k" aria-hidden="true">
        {theme === "dark" ? "☾" : "☀"}
      </span>
    </button>
  );
}

// --- RAG (dot + icon + label; never colour-alone, design §1.6) --------------
export type Rag = "green" | "amber" | "red" | "neutral";
const RAG_META: Record<Rag, { icon: string; label: string }> = {
  green: { icon: "▲", label: "On track" },
  amber: { icon: "◆", label: "At risk" },
  red: { icon: "▼", label: "Off track" },
  neutral: { icon: "—", label: "Unsupported" },
};
export function RagIndicator({ status, label }: { status: Rag; label?: string }): ReactNode {
  const meta = RAG_META[status];
  const tone = status === "neutral" ? "" : status; // .rag .green/.amber/.red
  return (
    <span role="status" style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
      <span className={`rag ${tone}`.trim()} aria-hidden="true" />
      <span aria-hidden="true">{meta.icon}</span>
      <span>{label ?? meta.label}</span>
    </span>
  );
}

/** The demo's bare RAG dot (.rag). Visually just a coloured dot, but carries an accessible label so
 *  status never reads by colour alone — it always sits beside the entity's name/text. */
export function RagDot({ status, label }: { status: Rag; label: string }): ReactNode {
  const tone = status === "neutral" ? "" : status;
  return <span className={`rag ${tone}`.trim()} role="img" aria-label={label} />;
}

/** Spine bar levels for a status (green=3 · amber=2 · red=1 · unsupported=0), per the demo's h.spine. */
export function spineLevels(status: Rag): Array<"g" | "a" | "r" | ""> {
  const filled = status === "green" ? 3 : status === "amber" ? 2 : status === "red" ? 1 : 0;
  const tone = status === "green" ? "g" : status === "amber" ? "a" : "r";
  return [0, 1, 2, 3].map((i) => (i < filled ? tone : ""));
}

/**
 * The health spine (.spine). When it is the primary status indicator, pass `label` so it is an
 * accessible image (status never reads by colour alone); omit `label` only where adjacent text
 * already states the status, in which case it is decorative.
 */
export function Spine({
  levels,
  label,
}: {
  levels: Array<"g" | "a" | "r" | "">;
  label?: string;
}): ReactNode {
  const bars = levels.map((l, i) => <i key={i} className={l} />);
  if (label) {
    return (
      <span className="spine" role="img" aria-label={label}>
        {bars}
      </span>
    );
  }
  return (
    <span className="spine" aria-hidden="true">
      {bars}
    </span>
  );
}

export type BasisKind = "empirical" | "estimate" | "hybrid" | "none";
const BASIS_META: Record<BasisKind, { label: string; sub: string }> = {
  empirical: { label: "Forecast from delivery history", sub: "empirical · throughput" },
  estimate: { label: "Estimate-based forecast", sub: "estimation · sized scope" },
  hybrid: { label: "Forecast from history + estimates", sub: "hybrid · both agree" },
  none: {
    label: "No credible basis — unsupported",
    sub: "neither estimation nor stable throughput",
  },
};
/** NX-2800 — the ONE mapping from a declared confidence basis to the chip kind. The portfolio and
 *  the project detail page must derive the chip through this same function; a second copy is how
 *  the list and detail screens end up disagreeing about the same project. */
export const basisKindOf = (basis: string | null): BasisKind =>
  basis === "throughput"
    ? "empirical"
    : basis === "estimation"
      ? "estimate"
      : basis === "hybrid"
        ? "hybrid"
        : "none";

/** Forecast-basis chip (.basis). M0 projects are typically `none` — honest: no delivery basis yet. */
export function Basis({ kind }: { kind: BasisKind }): ReactNode {
  const meta = BASIS_META[kind];
  return (
    <span className={`basis ${kind}`}>
      <span className="ic" aria-hidden="true" />
      {meta.label} <small>{meta.sub}</small>
    </span>
  );
}

// --- Confidence pill (.confpill bars + %; null => honest N/A) ---------------
export function ConfidencePill({ value }: { value: number | null }): ReactNode {
  if (value === null) {
    return (
      <span className="confpill" aria-label="Confidence not available">
        <span>N/A</span>
      </span>
    );
  }
  const pct = Math.round(value * 100);
  return (
    <span
      className="confpill"
      role="meter"
      aria-valuenow={pct}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={`Confidence ${pct}%`}
    >
      <span className="bars" aria-hidden="true">
        {[0, 1, 2, 3].map((i) => (
          <i key={i} style={{ height: `${5 + i * 2}px` }} />
        ))}
      </span>
      <span>{pct}%</span>
    </span>
  );
}

// --- Metric tile (.metric) -------------------------------------------------
/**
 * NX-2801 — the metric explanation, VISIBLE BY DEFAULT (supersedes NX-2798's hover-only ⓘ; Papa:
 * "the default should be displayed"). The topbar Explanations toggle hides every one of these at
 * once; the accessible name still names the metric so a screen reader hears which metric the
 * explanation belongs to.
 */
export function HelpTip({ text, label }: { text: string; label: string }): ReactNode {
  const visible = useExplanationsVisible();
  if (!visible) return null;
  return (
    <span className="mhelp" role="note" aria-label={`What ${label} means: ${text}`}>
      {text}
    </span>
  );
}

/** NX-2801 — the topbar switch governing every metric explanation. Default: shown. */
export function ExplanationsToggle(): ReactNode {
  const visible = useExplanationsVisible();
  return (
    <button
      type="button"
      className="lensbtn"
      role="switch"
      aria-checked={visible}
      aria-label="Toggle metric explanations"
      onClick={toggleExplanations}
    >
      <span className="eye" aria-hidden="true" />
      {visible ? "Hide explanations" : "Show explanations"}
    </button>
  );
}

export function MetricTile({
  label,
  value,
  confidence,
  help,
  signal,
}: {
  label: string;
  value: ReactNode;
  confidence?: number | null;
  /** Explanation override; when omitted the label is looked up in METRIC_HELP (NX-2798), so every
   *  tile with a known label explains itself with zero call-site wiring. */
  help?: string;
  /** NX-2802 — the RAW number for signal banding, in the unit the label's band documents. Omitted
   *  (or an unbanded label) renders neutral, exactly as before. */
  signal?: number;
}): ReactNode {
  const bandBasis = signalBasisFor(label);
  const baseHelp = help ?? helpFor(label);
  // The band's disclosed basis joins the explanation even when the value is neutral — the reader
  // should know the banding rule exists and where it comes from.
  const resolvedHelp =
    bandBasis === undefined
      ? baseHelp
      : baseHelp === undefined
        ? bandBasis
        : `${baseHelp} ${bandBasis}`;
  const tone = signal === undefined ? null : signalToneFor(label, signal);
  const pip =
    confidence == null ? null : confidence >= 0.8 ? "hi" : confidence >= 0.5 ? "mid" : "lo";
  // NX-2804 — tile anatomy: the data block (label → value → confidence) sits at a FIXED position
  // so every value in a grid row shares one scan line; the explanation renders LAST and is
  // bottom-pinned by CSS, so its variable length can never displace the number above it.
  return (
    <div className="metric">
      <div className="lab">{label}</div>
      <div className={tone === null ? "v num" : `v num sig-${tone}`}>
        {value}
        {tone !== null && (
          <span className={`sigbadge ${tone}`}>{tone === "good" ? "On track" : "Attention"}</span>
        )}
      </div>
      {confidence !== undefined && (
        <div className="mc">
          {pip ? (
            <>
              <span className={`pip ${pip}`} aria-hidden="true" />
              {Math.round((confidence ?? 0) * 100)}% confidence
            </>
          ) : (
            <span>Confidence N/A</span>
          )}
        </div>
      )}
      {resolvedHelp !== undefined && <HelpTip text={resolvedHelp} label={label} />}
    </div>
  );
}

// --- Card / Panel ----------------------------------------------------------
export function Card({ children, href }: { children: ReactNode; href?: string }): ReactNode {
  if (href) {
    return (
      <a className="card" href={href}>
        {children}
      </a>
    );
  }
  return <div className="card">{children}</div>;
}
export function Panel({ title, children }: { title?: ReactNode; children: ReactNode }): ReactNode {
  return (
    <section className="panel pad">
      {title !== undefined && <div className="sec">{title}</div>}
      {children}
    </section>
  );
}

// --- Drawer scaffold (.lineage + .det): locked demo drill-in-place shell ---
export function Drawer({
  open,
  title,
  subtitle,
  onClose,
  children,
}: {
  open: boolean;
  title: string;
  subtitle?: ReactNode;
  onClose: () => void;
  children: ReactNode;
}): ReactNode {
  return (
    <section
      className={`lineage${open ? " open" : ""}`}
      aria-hidden={!open}
      onKeyDown={(event) => {
        if (event.key === "Escape") onClose();
      }}
    >
      <div className={`det${open ? " open" : ""}`}>
        <div className="detwrap">
          <div className="inner detinner" role="dialog" aria-label={title} aria-modal="false">
            <div className="hd dh">
              <div className="lab">{title}</div>
              {subtitle !== undefined && <div className="src">{subtitle}</div>}
              <span
                className="x"
                role="button"
                tabIndex={0}
                aria-label="Close evidence drawer"
                onClick={onClose}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onClose();
                  }
                }}
              >
                ×
              </span>
            </div>
            {children}
          </div>
        </div>
      </div>
    </section>
  );
}
export function Tag({
  tone,
  children,
}: {
  tone?: "red" | "amber" | "green";
  children: ReactNode;
}): ReactNode {
  return <span className={`tag ${tone ?? ""}`.trim()}>{children}</span>;
}

// --- Honest no-data state (.na hatch + .nanote) ----------------------------
export function EmptyState({ message }: { message: string }): ReactNode {
  return (
    <div role="note">
      <div className="na" style={{ height: 56, borderRadius: 12 }} aria-hidden="true" />
      <div className="nanote">{message}</div>
    </div>
  );
}

export type HonestStateKind = "notobs" | "nodata" | "lowc" | "stale";
const HONEST_STATE: Record<HonestStateKind, { glyph: string; name: string }> = {
  notobs: { glyph: "⌀", name: "Not observable" },
  nodata: { glyph: "◔", name: "No data yet" },
  lowc: { glyph: "◑", name: "Low confidence" },
  stale: { glyph: "◷", name: "Stale" },
};
/**
 * A calm, honest data state (.state, per ADR-010): distinguishes "not observable" (N/A — the source
 * can't report it, never scored as zero) from "no data yet" (connected, no history) from low
 * confidence from stale. Never a blank, a crash, or a faked number.
 */
export function HonestState({
  kind,
  detail,
  action,
  title,
}: {
  kind: HonestStateKind;
  detail: string;
  action?: string;
  /** NX-2803 — when the state stands in for a TITLED metric tile, the metric's name must stay the
   *  header ("Mean time to resolve" above "No data yet"), not survive only inside the detail fine
   *  print. Untitled (page-level) states render exactly as before. */
  title?: string;
}): ReactNode {
  const meta = HONEST_STATE[kind];
  const body = (
    <div className={`state ${kind}`} role="note">
      <div className="gl" aria-hidden="true">
        {meta.glyph}
      </div>
      <div className="nm">{meta.name}</div>
      <div className="ds">{detail}</div>
      {action !== undefined && <div className="act">{action}</div>}
    </div>
  );
  if (title === undefined) return body;
  // NX-2804 — a titled honest state is a metric tile too: same anatomy, and when the glossary
  // knows the metric it still explains itself even while N/A.
  const stateHelp = helpFor(title);
  return (
    <div className="metric">
      <div className="lab">{title}</div>
      {body}
      {stateHelp !== undefined && <HelpTip text={stateHelp} label={title} />}
    </div>
  );
}

// --- Provenance popover (.prov + .pop): drill any figure to its source ------
export function ProvenancePopover({
  source,
  method,
  confidence,
  freshness,
  children,
}: {
  source: string;
  method: string;
  confidence: number | null;
  freshness?: string;
  children?: ReactNode;
}): ReactNode {
  const [open, setOpen] = useState(false);
  const openStyle = open
    ? { opacity: 1, visibility: "visible" as const, transform: "none" }
    : undefined;
  return (
    <span
      className="prov"
      tabIndex={0}
      role="button"
      aria-expanded={open}
      aria-label="Show provenance"
      onClick={(e) => {
        // The drill can sit inside a project-card link; toggle the popover in place rather than
        // letting the click bubble up and navigate the surrounding anchor.
        e.preventDefault();
        e.stopPropagation();
        setOpen((o) => !o);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          e.stopPropagation();
          setOpen((o) => !o);
        }
      }}
    >
      {children ?? "provenance"}
      <span className="pop" role="dialog" aria-label="Provenance" style={openStyle}>
        <span className="r">
          <span>Source</span>
          <b>{source}</b>
        </span>
        <span className="r">
          <span>Method</span>
          <b>{method}</b>
        </span>
        <span className="r">
          <span>Confidence</span>
          <b>{confidence === null ? "N/A" : `${Math.round(confidence * 100)}%`}</b>
        </span>
        {freshness !== undefined && (
          <span className="r">
            <span>Freshness</span>
            <b>{freshness}</b>
          </span>
        )}
      </span>
    </span>
  );
}
